import axios from 'axios';
import { getAuthHeaders } from '../utils/auth';
import { API_URL } from '../constants/urls';

export interface PlanningCycle {
  id: string;
  organization_id: string;
  cycle_type: '30-day' | 'quarter' | 'year' | '3-year';
  name: string;
  start_date: string;
  end_date: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  days_remaining?: number;
}

export interface KeyResult {
  id: string;
  okr_id: string;
  description: string;
  target_value: number;
  current_value: number;
  unit?: string;
  status: 'on-track' | 'at-risk' | 'blocked' | 'completed';
  owner_id?: string;
  order_index: number;
  reference_number?: number; // Sequential number within OKR (1, 2, 3)
  created_at: string;
  updated_at: string;
  progress_percentage?: number;
}

export interface OKRRelationship {
  id: string;
  okr_id: string;
  relationship_type:
    | 'organization'
    | 'position'
    | 'segment'
    | 'proposition'
    | 'group';
  related_object_id: string;
  contribution_notes?: string;
  created_at: string;
}

export interface OrganizationOKR {
  id: string;
  organization_id: string;
  planning_cycle_id: string;
  position_id?: string;
  parent_okr_id?: string;
  objective: string;
  description?: string;
  owner_id?: string;
  status: 'draft' | 'active' | 'completed' | 'cancelled';
  is_corporate?: boolean;
  order_index: number;
  reference_number?: number; // Human-friendly reference like OKR-42
  formatted_reference?: string; // Formatted as "OKR-42"
  created_at: string;
  updated_at: string;
  key_results: KeyResult[];
  relationships?: OKRRelationship[];
  child_okrs?: OrganizationOKR[];
  planning_cycle?: {
    id: string;
    name: string;
    cycle_type: string;
    start_date: string;
    end_date: string;
  };
}

export interface PlanningCycleWithOKRs extends PlanningCycle {
  okrs: OrganizationOKR[];
}

export interface PlanningConfiguration {
  cycle_types: string[];
  fiscal_year_start_month: number;
  available_cycle_types: string[];
}

export interface CurrentPlanningContext {
  current_cycle?: PlanningCycleWithOKRs;
  all_cycles: PlanningCycle[];
  next_review_date?: string;
  organization_settings: Record<string, any>;
  planning_configuration?: PlanningConfiguration;
}

export interface KeyResultProgress {
  value: number;
  notes?: string;
}

// Planning Configuration API
export const planningConfigApi = {
  // Get planning configuration
  async getConfig(organizationId: string): Promise<PlanningConfiguration> {
    const response = await axios.get(
      `${API_URL}/planning-cycles/config?organization_id=${organizationId}`,
      {
        headers: {
          ...getAuthHeaders(),
        },
      },
    );
    return response.data;
  },

  // Update planning configuration
  async updateConfig(
    organizationId: string,
    config: Partial<PlanningConfiguration>,
  ): Promise<{ message: string; cycles_generated: number }> {
    const response = await axios.post(
      `${API_URL}/planning-cycles/config?organization_id=${organizationId}`,
      config,
      {
        headers: {
          ...getAuthHeaders(),
        },
      },
    );
    return response.data;
  },

  // Generate cycles for organization
  async generateCycles(
    organizationId: string,
    targetYear?: number,
  ): Promise<{ message: string; cycles_generated: number }> {
    const params = new URLSearchParams({ organization_id: organizationId });
    if (targetYear) params.append('target_year', targetYear.toString());

    const response = await axios.post(
      `${API_URL}/planning-cycles/generate?${params}`,
      {},
      {
        headers: {
          ...getAuthHeaders(),
        },
      },
    );
    return response.data;
  },

  // Activate a cycle
  async activateCycle(
    organizationId: string,
    cycleId: string,
  ): Promise<{ message: string }> {
    const response = await axios.patch(
      `${API_URL}/planning-cycles/${cycleId}/activate?organization_id=${organizationId}`,
      {},
      {
        headers: {
          ...getAuthHeaders(),
        },
      },
    );
    return response.data;
  },
};

// Planning Cycle API
export const planningCyclesApi = {
  // Get all planning cycles
  async listCycles(
    organizationId: string,
    cycleType?: string,
    isActive?: boolean,
  ): Promise<PlanningCycle[]> {
    const params = new URLSearchParams({ organization_id: organizationId });
    if (cycleType) params.append('cycle_type', cycleType);
    if (isActive !== undefined) params.append('is_active', String(isActive));

    const response = await axios.get(`${API_URL}/planning-cycles?${params}`, {
      headers: {
        ...getAuthHeaders(),
      },
    });
    return response.data;
  },

  // Get current planning context
  async getCurrentContext(
    organizationId: string,
    cycleType?: string,
  ): Promise<CurrentPlanningContext> {
    const params = new URLSearchParams({ organization_id: organizationId });
    if (cycleType) params.append('cycle_type', cycleType);

    const response = await axios.get(
      `${API_URL}/planning-cycles/current?${params}`,
      {
        headers: {
          ...getAuthHeaders(),
        },
      },
    );
    return response.data;
  },

  // Get specific planning cycle with OKRs
  async getCycle(
    organizationId: string,
    cycleId: string,
  ): Promise<PlanningCycleWithOKRs> {
    const response = await axios.get(
      `${API_URL}/planning-cycles/${cycleId}?organization_id=${organizationId}`,
      {
        headers: {
          ...getAuthHeaders(),
        },
      },
    );
    return response.data;
  },

  // Create planning cycle (deprecated - use configuration instead)
  async createCycle(
    organizationId: string,
    cycle: Omit<
      PlanningCycle,
      'id' | 'organization_id' | 'created_at' | 'updated_at'
    >,
  ): Promise<PlanningCycle> {
    const response = await axios.post(
      `${API_URL}/planning-cycles?organization_id=${organizationId}`,
      cycle,
      {
        headers: {
          ...getAuthHeaders(),
        },
      },
    );
    return response.data;
  },

  // Update planning cycle
  async updateCycle(
    organizationId: string,
    cycleId: string,
    update: Partial<PlanningCycle>,
  ): Promise<PlanningCycle> {
    const response = await axios.patch(
      `${API_URL}/planning-cycles/${cycleId}?organization_id=${organizationId}`,
      update,
      {
        headers: {
          ...getAuthHeaders(),
        },
      },
    );
    return response.data;
  },

  // Delete planning cycle
  async deleteCycle(organizationId: string, cycleId: string): Promise<void> {
    await axios.delete(
      `${API_URL}/planning-cycles/${cycleId}?organization_id=${organizationId}`,
      {
        headers: {
          ...getAuthHeaders(),
        },
      },
    );
  },
};

// OKR API
export const okrApi = {
  // Create OKR for a planning cycle
  async createOKR(
    organizationId: string,
    cycleId: string,
    okr: {
      planning_cycle_id: string;
      position_id?: string;
      parent_okr_id?: string;
      objective: string;
      description?: string;
      owner_id?: string;
      status?: string;
      order_index?: number;
      key_results?: Omit<
        KeyResult,
        'id' | 'okr_id' | 'created_at' | 'updated_at'
      >[];
      relationships?: Omit<OKRRelationship, 'id' | 'okr_id' | 'created_at'>[];
    },
  ): Promise<OrganizationOKR> {
    const response = await axios.post(
      `${API_URL}/planning-cycles/${cycleId}/okrs?organization_id=${organizationId}`,
      okr,
      {
        headers: {
          ...getAuthHeaders(),
        },
      },
    );
    return response.data;
  },

  // Get specific OKR
  async getOKR(
    organizationId: string,
    okrId: string,
  ): Promise<OrganizationOKR> {
    const response = await axios.get(
      `${API_URL}/planning-cycles/okrs/${okrId}?organization_id=${organizationId}`,
      {
        headers: {
          ...getAuthHeaders(),
        },
      },
    );
    return response.data;
  },

  // Update OKR
  async updateOKR(
    organizationId: string,
    okrId: string,
    update: Partial<OrganizationOKR> & {
      relationships?: Omit<OKRRelationship, 'id' | 'okr_id' | 'created_at'>[];
    },
  ): Promise<OrganizationOKR> {
    const response = await axios.patch(
      `${API_URL}/planning-cycles/okrs/${okrId}?organization_id=${organizationId}`,
      update,
      {
        headers: {
          ...getAuthHeaders(),
        },
      },
    );
    return response.data;
  },

  // Delete OKR
  async deleteOKR(organizationId: string, okrId: string): Promise<void> {
    await axios.delete(
      `${API_URL}/planning-cycles/okrs/${okrId}?organization_id=${organizationId}`,
      {
        headers: {
          ...getAuthHeaders(),
        },
      },
    );
  },

  // Get position-level OKRs for a specific position ID
  async listPositionOKRs(
    organizationId: string,
    positionId: string,
    cycleId?: string,
    includeParent?: boolean,
  ): Promise<OrganizationOKR[]> {
    const params = new URLSearchParams({ organization_id: organizationId });
    if (cycleId) params.append('cycle_id', cycleId);
    if (includeParent) params.append('include_parent', 'true');

    const response = await axios.get(
      `${API_URL}/planning-cycles/positions/${positionId}/okrs?${params}`,
      {
        headers: {
          ...getAuthHeaders(),
        },
      },
    );
    return response.data;
  },

  // Create position-level OKR
  async createPositionOKR(
    organizationId: string,
    positionId: string,
    okr: {
      planning_cycle_id: string;
      parent_okr_id?: string;
      objective: string;
      description?: string;
      owner_id?: string;
      status?: string;
      order_index?: number;
      key_results?: Omit<
        KeyResult,
        'id' | 'okr_id' | 'created_at' | 'updated_at'
      >[];
      relationships?: Omit<OKRRelationship, 'id' | 'okr_id' | 'created_at'>[];
    },
  ): Promise<OrganizationOKR> {
    const response = await axios.post(
      `${API_URL}/planning-cycles/positions/${positionId}/okrs?organization_id=${organizationId}`,
      okr,
      {
        headers: {
          ...getAuthHeaders(),
        },
      },
    );
    return response.data;
  },

  // Get company-level OKRs
  async getCompanyOKRs(
    organizationId: string,
    cycleId?: string,
  ): Promise<OrganizationOKR[]> {
    const params = new URLSearchParams({ organization_id: organizationId });
    if (cycleId) params.append('cycle_id', cycleId);

    const response = await axios.get(
      `${API_URL}/planning-cycles/okrs/company?${params}`,
      {
        headers: {
          ...getAuthHeaders(),
        },
      },
    );
    return response.data;
  },

  // Get full OKR hierarchy
  async getOKRHierarchy(
    organizationId: string,
    cycleId?: string,
    cycleType?: string,
  ): Promise<OrganizationOKR[]> {
    const params = new URLSearchParams({ organization_id: organizationId });
    if (cycleId) params.append('cycle_id', cycleId);
    if (cycleType) params.append('cycle_type', cycleType);

    const response = await axios.get(
      `${API_URL}/planning-cycles/okrs/hierarchy?${params}`,
      {
        headers: {
          ...getAuthHeaders(),
        },
      },
    );
    return response.data;
  },

  // Get all OKRs within a planning cycle hierarchy
  async getPlanningCycleHierarchyOKRs(
    organizationId: string,
    cycleId: string,
    includeChildCycles = true,
  ): Promise<OrganizationOKR[]> {
    const params = new URLSearchParams({ organization_id: organizationId });
    if (includeChildCycles) params.append('include_child_cycles', 'true');

    const response = await axios.get(
      `${API_URL}/planning-cycles/${cycleId}/okrs/hierarchy?${params}`,
      {
        headers: {
          ...getAuthHeaders(),
        },
      },
    );
    return response.data;
  },
};

// Key Result API
export const keyResultApi = {
  // Create key result
  async createKeyResult(
    organizationId: string,
    okrId: string,
    keyResult: Omit<KeyResult, 'id' | 'okr_id' | 'created_at' | 'updated_at'>,
  ): Promise<KeyResult> {
    const response = await axios.post(
      `${API_URL}/planning-cycles/okrs/${okrId}/key-results?organization_id=${organizationId}`,
      keyResult,
      {
        headers: {
          ...getAuthHeaders(),
        },
      },
    );
    return response.data;
  },
};
